import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

import TaskGroupDrawer, { GroupTaskList } from 'modules/Tasks/Groups/TaskGroupDrawer';
import { Task, TaskGroupSummary, TaskStatuses } from 'modules/Tasks/types';
import { MEMBER_TASKS_QUERY_KEY } from 'services/memberTasks/queries/useMemberTasks';

interface MonthlyReimbursementGroupProps {
    readonly open: boolean;
    readonly onClose: () => void;
    readonly group: TaskGroupSummary;
}

// The reimbursement group is permanent (one per member, never recreated), so completed months would
// otherwise pile up in the panel forever. Keep active tasks always, and completed tasks only for a
// day after submission — long enough for the member to see the completed row (matching how normal
// group panels read across a refresh), after which they drop off so the list stays short.
const COMPLETED_VISIBLE_DAYS = 1;

function isActiveOrRecentlyCompleted(task: Task): boolean {
    if (task.status !== TaskStatuses.COMPLETED) {
        return true;
    }
    return !!task.completed_at && dayjs(task.completed_at).isAfter(dayjs().subtract(COMPLETED_VISIBLE_DAYS, 'day'));
}

/**
 * Custom group flow for the `monthly_reimbursement` task group. Rendering is entirely generic — the
 * standard drawer plus the default child task list — so the only reason this isn't a plain `expand`
 * group is the refetch below.
 *
 * Each month's reimbursement is completed on the reimbursement form, which the task's submit action
 * opens in a new tab (submit_action.is_external). The backend flips the task READY → COMPLETED there,
 * so when the member returns to this tab we refetch the member tasks to reflect the updated/completed
 * rows in the still-open panel (the group card + priority feed re-sync on drawer close via onClose).
 */
export default function MonthlyReimbursementGroup({ open, onClose, group }: Readonly<MonthlyReimbursementGroupProps>) {
    const queryClient = useQueryClient();

    useEffect(() => {
        if (!open) {
            return undefined;
        }
        const handleFocus = () => {
            queryClient.invalidateQueries({ queryKey: MEMBER_TASKS_QUERY_KEY });
        };
        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
    }, [open, queryClient]);

    return (
        <TaskGroupDrawer group={group} open={open} onClose={onClose}>
            <GroupTaskList group={group} filterTasks={isActiveOrRecentlyCompleted} />
        </TaskGroupDrawer>
    );
}
